import React from 'react';
import { Table, Button, Badge, ProgressBar } from 'react-bootstrap';
import moment from 'moment';

export const BudgetTable = ({ budgets, onDelete, onEdit, onAddExpense, budgetsWithSpent }) => {
   

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: {
                bg: 'success',
                icon: 'ri-check-double-line',
                label: 'Active'
            },
            over_budget: {
                bg: 'danger',
                icon: 'ri-alert-line',
                label: 'Over Budget'
            },
            expired: {
                bg: 'secondary',
                icon: 'ri-time-line',
                label: 'Expired'
            }
        };
        const config = statusConfig[status] || statusConfig.active;
        return (
            <Badge bg={config.bg} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                <i className={config.icon}></i>
                <span>{config.label}</span>
            </Badge>
        );
    };

    const getCategoryBadge = (category) => {
        const categoryConfig = {
            groceries: { bg: 'primary', icon: 'ri-shopping-basket-line' },
            utilities: { bg: 'info', icon: 'ri-flashlight-line' },
            rent: { bg: 'success', icon: 'ri-home-line' },
            entertainment: { bg: 'warning', icon: 'ri-gamepad-line' },
            transportation: { bg: 'danger', icon: 'ri-car-line' },
            healthcare: { bg: 'info', icon: 'ri-heart-pulse-line' },
            other: { bg: 'secondary', icon: 'ri-folder-line' }
        };
        const config = categoryConfig[category.toLowerCase()] || categoryConfig.other;
        return (
            <Badge bg={config.bg} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                <i className={config.icon}></i>
                <span>{category.charAt(0).toUpperCase() + category.slice(1)}</span>
            </Badge>
        );
    };

    const calculateProgress = (spent, amount) => {
        const spentAmount = Number(spent) || 0;
        const budgetAmount = Number(amount) || 1; // Prevent division by zero
        return Math.min((spentAmount / budgetAmount) * 100, 100);
    };

    const getDaysRemaining = (endDate) => {
        if (!endDate) return 0;
        const days = moment(endDate).diff(moment(), 'days');
        return days >= 0 ? days : 0;
    };

    const getProgressBarVariant = (progress, spent, amount) => {
        if (spent >= amount) return 'danger';
        if (progress >= 90) return 'warning';
        if (progress >= 75) return 'info';
        return 'success';
    };

    const formatCurrency = (amount) => {
        return Number(amount || 0).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <Table responsive hover className="table-striped">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Progress</th>
                    <th>Period</th>
                    <th>Status</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {budgetsWithSpent.map((budget) => {
                    const spent = Number(budget.spent) || 0;
                    const amount = Number(budget.amount) || 0;
                    const progress = calculateProgress(spent, amount);
                    const daysRemaining = getDaysRemaining(budget.endDate);
                    const progressVariant = getProgressBarVariant(progress, spent, amount);
                    
                    return (
                        <tr key={budget.id}>
                            <td>
                                <div className="fw-medium">{budget.name}</div>
                                <small className="text-muted">{budget.description}</small>
                            </td>
                            <td>{getCategoryBadge(budget.category || 'Other')}</td>
                            <td style={{ minWidth: '200px' }}>
                                <div className="d-flex justify-content-between mb-1">
                                    <small className="fw-medium">
                                        {formatCurrency(spent)} of {formatCurrency(amount)}
                                    </small>
                                    <small className={`fw-medium text-${progressVariant}`}>
                                        {progress.toFixed(1)}%
                                    </small>
                                </div>
                                <ProgressBar 
                                    now={progress} 
                                    variant={progressVariant}
                                    style={{ height: '6px' }}
                                />
                                <small className="text-muted d-block mt-1">
                                    <i className="ri-time-line me-1"></i>
                                    {daysRemaining} days remaining
                                </small>
                            </td>
                            <td>
                                <div className="fw-medium">
                                    {moment(budget.startDate).format('MMM DD')} - {moment(budget.endDate).format('MMM DD, YYYY')}
                                </div>
                                <small className="text-muted">
                                    {moment(budget.endDate).diff(moment(budget.startDate), 'days')} days period
                                </small>
                            </td>
                            <td>{getStatusBadge(budget.status)}</td>
                            <td>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-success" 
                                        size="sm"
                                        className="btn-icon"
                                        title="Add Expense"
                                        onClick={() => onAddExpense(budget.id)}
                                    >
                                        <i className="ri-add-circle-line"></i>
                                    </Button>
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        className="btn-icon"
                                        title="Edit Budget"
                                        onClick={() => onEdit(budget)}
                                    >
                                        <i className="ri-pencil-line"></i>
                                    </Button>
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        className="btn-icon"
                                        title="Delete Budget"
                                        onClick={() => onDelete(budget.id)}
                                    >
                                        <i className="ri-delete-bin-line"></i>
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    );
                })}
                {budgets.length === 0 && (
                    <tr>
                        <td colSpan="6" className="text-center py-4">
                            <i className="ri-inbox-line fs-24 text-muted d-block mb-2"></i>
                            <p className="text-muted mb-0">No budgets found</p>
                        </td>
                    </tr>
                )}
            </tbody>
        </Table>
    );
}; 